<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { X } from 'lucide-vue-next';
import { useForm } from 'vee-validate';
import { computed, watch } from 'vue';

import type { AddToBlocklistForm } from '../schemas/blocklistSchema';

import { addToBlocklistSchema } from '../schemas/blocklistSchema';

export interface AddToBlocklistModalProps {
  isLoading?: boolean;
  modelValue: boolean;
}

const props = withDefaults(defineProps<AddToBlocklistModalProps>(), {
  isLoading: false,
});

const emit = defineEmits<{
  'submit': [data: AddToBlocklistForm];
  'update:modelValue': [value: boolean];
}>();

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value);
  },
});

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
} = useForm<AddToBlocklistForm>({
  validationSchema: toTypedSchema(addToBlocklistSchema),
});

const [ccb, ccbProps] = defineField('ccb');
const [reason, reasonProps] = defineField('reason');

const onSubmit = handleSubmit((values) => {
  emit('submit', values);
});

function handleCancel(): void {
  if (!props.isLoading) {
    isVisible.value = false;
    resetForm();
  }
}

function handleClose(): void {
  if (!props.isLoading) {
    handleCancel();
  }
}

watch(isVisible, (newValue) => {
  if (!newValue) {
    resetForm();
  }
});
</script>

<template>
  <div
    v-if="isVisible"
    class="modal modal-blur fade show"
    style="display: block;"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @keydown.escape="handleClose"
  >
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">Adicionar Financiamento à Lista de Bloqueios</h5>
          <button
            v-if="!isLoading"
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="handleClose"
          >
            <X :size="20" />
          </button>
        </div>

        <form @submit.prevent="onSubmit">
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label required" for="ccb">CCB</label>
              <input
                id="ccb"
                v-model="ccb"
                v-bind="ccbProps"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.ccb }"
                placeholder="Digite o número da CCB"
                :disabled="isLoading"
              />
              <div v-if="errors.ccb" class="invalid-feedback">
                {{ errors.ccb }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label required" for="reason">Motivo do Bloqueio</label>
              <textarea
                id="reason"
                v-model="reason"
                v-bind="reasonProps"
                class="form-control"
                :class="{ 'is-invalid': errors.reason }"
                rows="4"
                placeholder="Digite o motivo pelo qual este financiamento está sendo bloqueado (mínimo 10 caracteres)"
                :disabled="isLoading"
              />
              <div v-if="errors.reason" class="invalid-feedback">
                {{ errors.reason }}
              </div>
              <small class="form-hint">Mínimo de 10 caracteres</small>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="isLoading"
              @click="handleCancel"
            >
              Cancelar
            </button>

            <button
              type="submit"
              class="btn btn-primary"
              :disabled="isLoading"
            >
              <span
                v-if="isLoading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              {{ isLoading ? 'Adicionando...' : 'Adicionar' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <button
      type="button"
      class="modal-backdrop fade show"
      @click="handleClose"
    />
  </div>
</template>

<style scoped lang="scss">
.modal {
  z-index: 1055;

  &.show {
    display: block !important;
  }

  .modal-dialog {
    z-index: 1060;
    position: relative;
  }
}

.modal-backdrop {
  z-index: 1050;
}

.modal-header {
  background-color: var(--bs-light);
  border-bottom: 1px solid var(--bs-border-color);

  .modal-title {
    font-weight: 600;
    color: var(--bs-dark);
    display: flex;
    align-items: center;
  }
}

.btn-close {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--bs-secondary);
  transition: color 0.15s ease-in-out;

  &:hover {
    color: var(--bs-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.modal-footer {
  background-color: var(--bs-light);
  border-top: 1px solid var(--bs-border-color);

  .btn {
    min-width: 100px;
  }
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.form-label.required::after {
  content: ' *';
  color: var(--bs-danger);
}
</style>
